/**
 * LocaleProvider
 *
 * Extracts locale from URL params and applies it to i18n.
 * Syncs URL locale with i18n language state.
 */

import { useEffect, ReactNode, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { isValidLocale } from "../shared/constants/locales";
import { logger } from "../shared/utils/logger";

interface LocaleProviderProps {
  children: ReactNode;
}

export function LocaleProvider({ children }: LocaleProviderProps) {
  const { locale: urlLocale } = useParams<{ locale?: string }>();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isLanguageSet, setIsLanguageSet] = useState(false);

  logger.log('[LocaleProvider] Render - urlLocale:', urlLocale, 'isLanguageSet:', isLanguageSet, 'i18n.language:', i18n.language);

  useEffect(() => {
    logger.log('[LocaleProvider] Effect triggered - urlLocale:', urlLocale, 'pathname:', location.pathname);

    // Only the first language blocks render. Resetting here on a mid-flow switch would unmount
    // the flow while a lazily loaded locale arrives (VEX-8108).

    // If URL has a locale param, validate and apply it
    if (urlLocale) {
      // If URL locale is invalid, redirect to English (en)
      if (!isValidLocale(urlLocale)) {
        logger.log('[LocaleProvider] Invalid locale in URL:', urlLocale, '- redirecting to /en');
        const newPath = location.pathname.replace(`/${urlLocale}`, '/en');
        navigate(newPath, { replace: true });
        return;
      }

      // Apply valid locale to i18n
      logger.log('[LocaleProvider] Setting locale from URL:', urlLocale);
      logger.log('[LocaleProvider] Current i18n language:', i18n.language);

      // i18next can report the language before its lazily loaded bundle arrives
      if (i18n.language === urlLocale && i18n.hasResourceBundle(urlLocale, 'translation')) {
        logger.log('[LocaleProvider] Language already set to:', urlLocale);
        setIsLanguageSet(true);
        return;
      }

      // Change language and wait for it to be applied
      logger.log('[LocaleProvider] Calling i18n.changeLanguage to:', urlLocale);
      i18n.changeLanguage(urlLocale).then(() => {
        logger.log('[LocaleProvider] Language changed successfully to:', urlLocale);
        logger.log('[LocaleProvider] i18n.language after change:', i18n.language);
        logger.log('[LocaleProvider] Available languages:', Object.keys(i18n.store.data));
        logger.log('[LocaleProvider] Translation test for es:', i18n.t('face.ready.heading', { lng: urlLocale }));
        logger.log('[LocaleProvider] Translation test current:', i18n.t('face.ready.heading'));
        setIsLanguageSet(true);
      }).catch((error) => {
        logger.error('[LocaleProvider] Error changing language:', error);
        setIsLanguageSet(true); // Still allow rendering even if language change fails
      });
    } else {
      // No locale in URL - redirect to English
      if (
        location.pathname === "/face-liveness" ||
        location.pathname === "/legal-id-verification"
      ) {
        logger.log('[LocaleProvider] No locale in URL, redirecting to /en');
        navigate(`/en${location.pathname}`, { replace: true });
      } else {
        // For other routes (like /testing), just set the flag
        logger.log('[LocaleProvider] No locale needed for this route, setting flag');
        setIsLanguageSet(true);
      }
    }
  }, [urlLocale, location.pathname, i18n, navigate]);

  // Don't render children until language is set (only for routes that need locale)
  if (urlLocale && !isLanguageSet) {
    logger.log('[LocaleProvider] Blocking render - waiting for language to be set');
    return null;
  }

  logger.log('[LocaleProvider] Rendering children');
  return <>{children}</>;
}
